import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert, Card } from 'react-bootstrap';
import styled from 'styled-components';
import axios from 'axios';
import { isInIframe } from '../../utils/is-in-iframe';
import { ToolsHeader } from '../components/ToolsHeader';

const Box = styled.div`
  font-family: Arial, sans-serif;
  margin: 20px auto;
  max-width: 900px;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 40px 20px;
  background-color: #ffffff;
  transition: box-shadow 0.5s ease;

  &.interDisplay {
    font-family: InterDisplay;
  }
  &.defaultText {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
      Ubuntu, Cantarell, sans-serif;
  }

  h1 {
    text-align: center;
    margin-bottom: 20px;
  }

  .result-section {
    margin-top: 30px;
  }

  .card {
    border-radius: 12px;
    padding: 10px 0;
    background-color: #fff;
    border: 1px solid #e9ecef;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: default;
    border-top: 4px solid #8CC540;
  }

  .card-title {
    font-size: 0.95rem;
    color: #555;
    font-weight: 500;
  }

  .card-value {
    font-size: 1.8rem;
    color: #8CC540;
    font-weight: 700;
    margin-top: 10px;
  }
`;

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const WebsiteAuthorityChecker: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<null | {
    domain: string;
    backlinks: number;
    age: number;
    authorityScore: number;
    pageAuthority: number;
    spamScore: number;
    qualityBacklinks: number;
    rankingKeywords: number;
    referringDomains: number;
  }>(null);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: 'setHeight', height }, '*');
  }, [result]);

  const handleCheckAuthority = async () => {
    if (!domain) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const baseUrl = process.env.REACT_APP_BASE_API;
      const response = await axios.get(
        `${baseUrl}/api/authority-checker/authority?domain=${domain}`
      );
      setResult(response.data);
    } catch (err: any) {
      setError('Failed to fetch authority data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDomain('');
    setError('');
    setResult(null);
  };

  return (
    <Box className={`${!isInIframe ? 'defaultText' : 'interDisplay'}`}>
      <div className={`container_domain ${isInIframe && 'interDisplay'}`}>
        {!isInIframe && (
          <ToolsHeader
            title="Website Authority Checker"
            description="Check your website’s authority, credibility, and SEO performance metrics including backlinks, domain age, and spam score."
          />
        )}

        <Container>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleCheckAuthority();
            }}
          >
            <Row className="gx-2 mb-3">
              <Col xs={12} md={8} className="mb-2 mb-md-0">
                <Form.Control
                  type="text"
                  placeholder="Enter domain (e.g., example.com)"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  style={{
                    height: '52px',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: '2px solid #e9ecef',
                    backgroundColor: '#ffffff',
                    color: '#212529',
                    transition: 'all 0.3s ease',
                  }}
                />
              </Col>
              <Col xs={12} md={4}>
                <Button
                  variant="success"
                  onClick={handleCheckAuthority}
                  style={{
                    height: '52px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    width: '100%',
                    borderRadius: '8px',
                    backgroundColor: '#8CC540',
                    border: 'none',
                    color: '#fff',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(140, 197, 64, 0.3)',
                  }}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" animation="border" /> : 'Check Authority'}
                </Button>
              </Col>
            </Row>
          </Form>

          <div
            className="text-center mb-3"
            style={{ color: '#6c757d', fontSize: '0.9rem' }}
          >
            For example:{' '}
            <span style={{ color: '#8CC540', fontWeight: 600 }}>coozmoo.com</span>,{' '}
            <span style={{ color: '#8CC540', fontWeight: 600 }}>performancedashboard.io</span>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {result && (
            <div className="result-section text-center mt-5">
              <h4
                style={{
                  color: '#343a40',
                  fontWeight: 600,
                  marginBottom: '20px',
                }}
              >
                Website Authority:{' '}
                <a
                  href={`https://${result.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#8CC540',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {result.domain}
                </a>
              </h4>

              <Row className="g-4 justify-content-center">
                {[
                  { label: 'Domain Authority', value: formatNumber(result.authorityScore) },
                  { label: 'Page Authority', value: formatNumber(result.pageAuthority) },
                  { label: 'Total Backlinks', value: formatNumber(result.backlinks) },
                  { label: 'Referring Domains', value: formatNumber(result.referringDomains) },
                  { label: 'Quality Backlinks', value: `${result.qualityBacklinks}%` },
                  { label: 'Spam Score', value: `${result.spamScore}%` },
                  { label: 'Ranking Keywords', value: formatNumber(result.rankingKeywords) },
                  { label: 'Domain Age', value: `${result.age} years` },
                ].map((item, index) => (
                  <Col xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card className="card">
                      <Card.Body>
                        <Card.Title className="card-title">{item.label}</Card.Title>
                        <Card.Text className="card-value">{item.value}</Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <div className="mt-4">
                <Button
                  onClick={handleReset}
                  style={{
                    backgroundColor: '#41490f',
                    borderRadius: '8px',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 24px',
                    fontWeight: 600,
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          )}
        </Container>
      </div>
    </Box>
  );
};

export default WebsiteAuthorityChecker;
